import {
  ProviderSettingsRequestError,
  requestProviderSettings,
} from '@/services/provider/settings-request';
import type {
  ProviderBrandingSettings,
  UpdateProviderBrandingInput,
} from '@/types/provider-branding';

type ApiPayload = {
  data?: unknown;
  message?: string;
  errors?: Record<string, string[]>;
};

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const readBoolean = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

export function normalizeBrandingField(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeOptionalString(value: unknown): string | null {
  const raw = readString(value)?.trim() ?? '';
  return raw.length > 0 ? raw : null;
}

function normalizeSettings(payload: ApiPayload | undefined): ProviderBrandingSettings {
  const record =
    (payload?.data as Record<string, unknown> | undefined) ??
    ((payload as Record<string, unknown> | undefined) ?? {});

  return {
    providerName: normalizeOptionalString(record.provider_name ?? record.providerName),
    providerUrl: normalizeOptionalString(record.provider_url ?? record.providerUrl),
    isActive: readBoolean(record.is_active ?? record.isActive) ?? false,
    logoUrl: normalizeOptionalString(record.logo_url ?? record.logoUrl),
    logoLightUrl: normalizeOptionalString(record.logo_light_url ?? record.logoLightUrl),
    logoDarkUrl: normalizeOptionalString(record.logo_dark_url ?? record.logoDarkUrl),
    primaryColor: normalizeOptionalString(record.primary_color ?? record.primaryColor),
    accentColor: normalizeOptionalString(record.accent_color ?? record.accentColor),
  };
}

export class ProviderBrandingRequestError extends ProviderSettingsRequestError {
  errors?: Record<string, string[]>;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message, status, payload);
    if (payload && typeof payload === 'object' && 'errors' in (payload as Record<string, unknown>)) {
      this.errors = (payload as { errors?: Record<string, string[]> }).errors;
    }
  }
}

async function requestProviderBranding(
  path: string,
  options: { method?: 'GET' | 'PUT'; body?: Record<string, unknown> | undefined } = {},
): Promise<ApiPayload> {
  try {
    return (await requestProviderSettings(path, options)) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ProviderBrandingRequestError(error.message, error.status, error.payload);
    }
    throw new ProviderBrandingRequestError('Provider branding request failed', undefined, error);
  }
}

const EMPTY_SETTINGS: ProviderBrandingSettings = {
  providerName: null,
  providerUrl: null,
  isActive: false,
  logoUrl: null,
  logoLightUrl: null,
  logoDarkUrl: null,
  primaryColor: null,
  accentColor: null,
};

class ProviderBrandingService {
  async getSettings(): Promise<ProviderBrandingSettings> {
    try {
      const response = await requestProviderBranding('/api/v1/provider/settings/branding');
      return normalizeSettings(response);
    } catch (error) {
      if (error instanceof ProviderBrandingRequestError && (error.status ?? 0) === 404) {
        return { ...EMPTY_SETTINGS };
      }
      throw error;
    }
  }

  async updateSettings(
    input: UpdateProviderBrandingInput,
  ): Promise<ProviderBrandingSettings> {
    const body: Record<string, unknown> = {
      is_active: input.isActive,
      logo_url: input.logoUrl,
      primary_color: input.primaryColor,
      accent_color: input.accentColor,
    };
    if (input.logoLightUrl !== undefined) {
      body.logo_light_url = input.logoLightUrl;
    }
    if (input.logoDarkUrl !== undefined) {
      body.logo_dark_url = input.logoDarkUrl;
    }

    const response = await requestProviderBranding('/api/v1/provider/settings/branding', {
      method: 'PUT',
      body,
    });

    return normalizeSettings(response);
  }
}

export const providerBrandingService = new ProviderBrandingService();
